import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { type ColumnDef } from '@tanstack/react-table'
import { MessageSquare, RefreshCw, Send } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  DataTable,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  StatusBadge,
  Textarea,
} from '@/components/ui'
import { useToast } from '@/components/ui'

// ─── Types ───────────────────────────────────────────────────────────────────

interface PaperclipIssue {
  id: string
  identifier: string
  title: string
  description?: string
  status: string
  priority: string
  createdAt: string
  updatedAt?: string
  labels?: Array<{ id: string; name: string; color?: string }>
  assignee?: { id: string; name: string } | null
}

interface PaperclipComment {
  id: string
  body: string
  authorName?: string
  createdAt: string
}

// ─── API helpers ──────────────────────────────────────────────────────────────

async function fetchIssues(): Promise<PaperclipIssue[]> {
  const { data } = await apiClient.get('/paperclip/issues')
  return data
}

async function fetchIssue(id: string): Promise<PaperclipIssue> {
  const { data } = await apiClient.get(`/paperclip/issues/${id}`)
  return data
}

async function fetchComments(issueId: string): Promise<PaperclipComment[]> {
  const { data } = await apiClient.get(`/paperclip/issues/${issueId}/comments`)
  return data
}

async function postComment(issueId: string, body: string): Promise<void> {
  await apiClient.post(`/paperclip/issues/${issueId}/comments`, { body })
}

// ─── Status label helper ──────────────────────────────────────────────────────

const STATUS_LABELS: Record<string, string> = {
  todo: 'Offen',
  in_progress: 'In Arbeit',
  in_review: 'In Review',
  blocked: 'Blockiert',
  done: 'Erledigt',
  cancelled: 'Abgebrochen',
  backlog: 'Backlog',
}

function statusLabel(s: string): string {
  return STATUS_LABELS[s] ?? s
}

// ─── Column definitions ───────────────────────────────────────────────────────

const columns: ColumnDef<PaperclipIssue>[] = [
  {
    accessorKey: 'identifier',
    header: 'ID',
    size: 100,
    cell: ({ row }) => (
      <span className="font-mono text-xs text-muted-foreground">{row.original.identifier}</span>
    ),
  },
  {
    accessorKey: 'title',
    header: 'Titel',
    cell: ({ row }) => (
      <span className="font-medium">{row.original.title}</span>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    size: 120,
    cell: ({ row }) => (
      <StatusBadge status={row.original.status} label={statusLabel(row.original.status)} />
    ),
  },
  {
    accessorKey: 'priority',
    header: 'Priorität',
    size: 100,
    cell: ({ row }) => (
      <span className="text-sm capitalize">{row.original.priority}</span>
    ),
  },
  {
    accessorKey: 'assignee',
    header: 'Zuständig',
    size: 150,
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground">
        {row.original.assignee?.name ?? '—'}
      </span>
    ),
  },
  {
    accessorKey: 'createdAt',
    header: 'Erstellt',
    size: 120,
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground">
        {new Date(row.original.createdAt).toLocaleDateString('de-DE')}
      </span>
    ),
  },
]

// ─── Comment section ──────────────────────────────────────────────────────────

function IssueComments({ issue }: { issue: PaperclipIssue }) {
  const [commentText, setCommentText] = useState('')
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const commentsQuery = useQuery({
    queryKey: ['paperclip-comments', issue.id],
    queryFn: () => fetchComments(issue.id),
  })

  const mutation = useMutation({
    mutationFn: (body: string) => postComment(issue.id, body),
    onSuccess: () => {
      setCommentText('')
      void queryClient.invalidateQueries({ queryKey: ['paperclip-comments', issue.id] })
      toast({ title: 'Kommentar gesendet' })
    },
    onError: (err) => {
      toast({
        title: 'Fehler beim Senden',
        description: err instanceof Error ? err.message : 'Unbekannter Fehler',
        variant: 'destructive',
      })
    },
  })

  return (
    <div className="flex flex-col gap-4">
      <h3 className="flex items-center gap-2 text-sm font-semibold">
        <MessageSquare className="h-4 w-4" />
        Kommentare
      </h3>

      {commentsQuery.isPending && <LoadingState label="Kommentare werden geladen…" />}
      {commentsQuery.isError && (
        <ErrorState
          title="Kommentare konnten nicht geladen werden"
          details={commentsQuery.error instanceof Error ? commentsQuery.error.message : undefined}
        />
      )}

      {commentsQuery.data && commentsQuery.data.length === 0 && (
        <p className="text-sm text-muted-foreground">Noch keine Kommentare.</p>
      )}

      {commentsQuery.data && commentsQuery.data.length > 0 && (
        <div className="flex flex-col gap-3">
          {commentsQuery.data.map((c) => (
            <div key={c.id} className="rounded-md border bg-muted/30 p-3 text-sm">
              <div className="mb-1 flex items-center justify-between">
                <span className="font-medium">{c.authorName ?? 'Agent'}</span>
                <span className="text-xs text-muted-foreground">
                  {new Date(c.createdAt).toLocaleString('de-DE')}
                </span>
              </div>
              <p className="whitespace-pre-wrap text-muted-foreground">{c.body}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-2 pt-2">
        <Textarea
          label="Antwort schreiben"
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          placeholder="Kommentar eingeben…"
          rows={3}
        />
        <div className="flex justify-end">
          <Button
            variant="primary"
            size="sm"
            disabled={!commentText.trim() || mutation.isPending}
            onClick={() => mutation.mutate(commentText)}
          >
            <Send className="mr-1 h-3.5 w-3.5" />
            Senden
          </Button>
        </div>
      </div>
    </div>
  )
}

// ─── Issue detail panel ───────────────────────────────────────────────────────

function IssueDetail({ issueId, onClose }: { issueId: string; onClose: () => void }) {
  const issueQuery = useQuery({
    queryKey: ['paperclip-issue', issueId],
    queryFn: () => fetchIssue(issueId),
  })

  return (
    <div className="flex flex-col gap-4 overflow-y-auto">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={onClose}>
          ← Zurück
        </Button>
      </div>

      {issueQuery.isPending && <LoadingState label="Issue wird geladen…" />}
      {issueQuery.isError && (
        <ErrorState
          title="Issue konnte nicht geladen werden"
          details={issueQuery.error instanceof Error ? issueQuery.error.message : undefined}
        />
      )}

      {issueQuery.data && (
        <>
          <div>
            <p className="font-mono text-xs text-muted-foreground">{issueQuery.data.identifier}</p>
            <h2 className="mt-1 text-lg font-semibold">{issueQuery.data.title}</h2>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <StatusBadge status={issueQuery.data.status} label={statusLabel(issueQuery.data.status)} />
              <span className="text-xs text-muted-foreground capitalize">
                Priorität: {issueQuery.data.priority}
              </span>
              {issueQuery.data.assignee && (
                <span className="text-xs text-muted-foreground">
                  Zuständig: {issueQuery.data.assignee.name}
                </span>
              )}
            </div>
          </div>

          {issueQuery.data.description && (
            <div className="rounded-md border bg-muted/20 p-3 text-sm">
              <p className="whitespace-pre-wrap text-muted-foreground">{issueQuery.data.description}</p>
            </div>
          )}

          {issueQuery.data.labels && issueQuery.data.labels.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {issueQuery.data.labels.map((l) => (
                <span
                  key={l.id}
                  className="rounded-full px-2 py-0.5 text-xs font-medium"
                  style={l.color ? { backgroundColor: l.color + '22', color: l.color } : undefined}
                >
                  {l.name}
                </span>
              ))}
            </div>
          )}

          <IssueComments issue={issueQuery.data} />
        </>
      )}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function PaperclipPage() {
  const [selectedIssueId, setSelectedIssueId] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const issuesQuery = useQuery({
    queryKey: ['paperclip-issues'],
    queryFn: fetchIssues,
  })

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader
        title="Paperclip Issues"
        description="Interne Aufgaben und Issues aus Paperclip"
        actions={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void queryClient.invalidateQueries({ queryKey: ['paperclip-issues'] })}
          >
            <RefreshCw className="mr-1 h-4 w-4" />
            Aktualisieren
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Issue list */}
        <div className="flex flex-col gap-2">
          {issuesQuery.isPending && <LoadingState label="Issues werden geladen…" />}
          {issuesQuery.isError && (
            <ErrorState
              title="Issues konnten nicht geladen werden"
              details={issuesQuery.error instanceof Error ? issuesQuery.error.message : undefined}
            />
          )}
          {issuesQuery.data && issuesQuery.data.length === 0 && (
            <EmptyState
              title="Keine Issues vorhanden"
              description="Es wurden keine Issues im konfigurierten Paperclip-Projekt gefunden."
            />
          )}
          {issuesQuery.data && issuesQuery.data.length > 0 && (
            <DataTable
              columns={columns}
              data={issuesQuery.data}
              onRowClick={(row) => setSelectedIssueId(row.id)}
              rowClassName={(row) =>
                row.id === selectedIssueId ? 'bg-muted/50 cursor-pointer' : 'cursor-pointer'
              }
            />
          )}
        </div>

        {/* Issue detail / comments */}
        <div className="rounded-lg border p-4">
          {selectedIssueId ? (
            <IssueDetail issueId={selectedIssueId} onClose={() => setSelectedIssueId(null)} />
          ) : (
            <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
              Issue auswählen, um Details und Kommentare anzuzeigen
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
